import { run as runCelebration } from "../celebrate/sequence.js";
import {
  createWidgetHideFrames,
  createWidgetPanelEntranceFrames,
  createWidgetPeekFrames,
  createWidgetRevealFrames,
  createWidgetReturnFrames,
  WIDGET_REVEAL_DURATION,
  WIDGET_RETURN_DURATION,
} from "../../core/widget-engagement-keyframes.js";
import {
  announceWidgetState,
  QUIZ_CORRECT_SIGNAL,
  WIDGET_ENGAGEMENT_SEQUENCE,
} from "../../core/widget-engagement.js";

export { QUIZ_CORRECT_SIGNAL };

export const meta = Object.freeze({
  id: WIDGET_ENGAGEMENT_SEQUENCE,
  label: "Widget-Peek · Quiz korrekt",
  duration: 5200,
  managesOwnLoop: true,
  description: "Orbi schaut hinter dem Chat-Widget hervor, reagiert auf ein Quiz-Signal und feiert anschließend.",
  tags: ["widget", "peek", "idle", "learning", "quiz", "signal", "celebration", "interactive"],
});

function currentTransform(element, fallback) {
  const value = window.getComputedStyle(element).transform;
  return value && value !== "none" ? value : fallback;
}

async function awaitAnimations(animations) {
  await Promise.allSettled(animations.map(({ finished }) => finished));
}

export async function run(context) {
  const {
    root,
    elements,
    scene,
    animate,
    signal,
    reducedMotion,
    waitForSignal,
  } = context;

  if (!scene?.learningWidget || !scene.effectsRoot) {
    throw new Error("Der Widget-Peek benötigt die Chat-Widget-Fläche und den Stage-Effekt-Layer.");
  }
  if (typeof waitForSignal !== "function") {
    throw new Error("Der Widget-Peek benötigt die externe Controller-Signal-Schnittstelle.");
  }

  announceWidgetState(root, "starting");

  const entranceOptions = { duration: 760, easing: "linear" };
  await awaitAnimations([
    animate(scene.learningWidget, createWidgetPanelEntranceFrames(), entranceOptions),
    animate(root, createWidgetHideFrames(), entranceOptions),
  ]);

  let cycleIndex = 0;

  try {
    while (!signal?.aborted) {
      announceWidgetState(root, "waiting");
      const quizSignal = waitForSignal(QUIZ_CORRECT_SIGNAL);
      let quizResult = null;
      let revealFrom = null;

      if (reducedMotion) {
        quizResult = await quizSignal;
      } else {
        while (!quizResult && !signal?.aborted) {
          const peek = createWidgetPeekFrames(cycleIndex);
          const peekOptions = { duration: peek.duration, easing: "linear", transient: true };
          const peekAnimations = [
            animate(root, peek.root, peekOptions),
            animate(elements.eyes, peek.eyes, peekOptions),
            animate(elements.blink, peek.blink, peekOptions),
          ];
          const cycleFinished = awaitAnimations(peekAnimations).then(() => ({ type: "cycle" }));
          const eventReceived = quizSignal.then((result) => ({ type: "event", result }));
          const winner = await Promise.race([cycleFinished, eventReceived]);

          if (winner.type === "event") {
            quizResult = winner.result;
            revealFrom = currentTransform(root, createWidgetHideFrames().at(-1).transform);
            peekAnimations.forEach((animation) => animation.cancel());
          } else {
            cycleIndex += 1;
          }
        }
      }

      if (!quizResult || quizResult.aborted || signal?.aborted) break;

      announceWidgetState(root, "revealing");
      const reveal = createWidgetRevealFrames(revealFrom);
      const revealOptions = { duration: WIDGET_REVEAL_DURATION, easing: "linear" };
      await awaitAnimations([
        animate(root, reveal.root, revealOptions),
        animate(elements.actor, reveal.actor, revealOptions),
        animate(elements.shadow, reveal.shadow, revealOptions),
        animate(elements.eyes, reveal.eyes, revealOptions),
        animate(scene.effectsRoot, reveal.effects, revealOptions),
      ]);
      if (signal?.aborted) break;

      announceWidgetState(root, "celebrating");
      await runCelebration(context);
      if (signal?.aborted) break;

      announceWidgetState(root, "returning");
      const returning = createWidgetReturnFrames();
      const returnOptions = { duration: WIDGET_RETURN_DURATION, easing: "linear" };
      await awaitAnimations([
        animate(root, returning.root, returnOptions),
        animate(elements.actor, returning.actor, returnOptions),
        animate(elements.shadow, returning.shadow, returnOptions),
        animate(scene.effectsRoot, returning.effects, returnOptions),
      ]);
      cycleIndex += 1;
    }
  } finally {
    announceWidgetState(root, "inactive");
  }
}
